import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function AddPlayerModal({ teamId, onPlayerAdded, mode = 'add', show, onHide, selectedPlayer: propSelectedPlayer }) {
    const [players, setPlayers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPlayer, setSelectedPlayer] = useState(propSelectedPlayer);
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [filterStatus, setFilterStatus] = useState('available');
    const [searchTimeout, setSearchTimeout] = useState(null);

    /* -----------------------------------------------------------
       Sync selectedPlayer when parent updates it
    ----------------------------------------------------------- */
    useEffect(() => {
        setSelectedPlayer(propSelectedPlayer);
    }, [propSelectedPlayer]);

    /* -----------------------------------------------------------
       1) Fetch available players  ───────────────────────────────
    ----------------------------------------------------------- */
    const fetchAvailablePlayers = useCallback(
        async (search = searchTerm) => {
            setLoading(true);
            try {
                const response = await axios.get('/api/player/available/', {
                    withCredentials: true,
                    params: {
                        search,
                        team: teamId,
                        status: filterStatus,
                    },
                });
                setPlayers(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching available players:', err);
                setError(
                    err.response?.status === 401
                        ? 'Session expired. Please refresh the page and try again.'
                        : 'Failed to load available players'
                );
            } finally {
                setLoading(false);
            }
        },
        [teamId, filterStatus, searchTerm] /* ⊙ include all referenced values */
    );

    /* -----------------------------------------------------------
       2) Fetch teams (for transfer mode)  ───────────────────────
    ----------------------------------------------------------- */
    const fetchTeams = useCallback(async () => {
        try {
            const response = await axios.get('/api/teams/', {
                withCredentials: true,
            });
            const filtered = (response.data.results || response.data).filter(
                (t) => t.id !== parseInt(teamId, 10)
            );
            setTeams(filtered);
            setError(null);
        } catch (err) {
            console.error('Error fetching teams:', err);
            setError('Failed to load teams');
        }
    }, [teamId]); /* ⊙ memoise & include teamId */

    /* -----------------------------------------------------------
       3) Trigger data fetch when modal opens or filter changes
    ----------------------------------------------------------- */
    useEffect(() => {
        if (!show) return;

        if (mode === 'transfer') {
            fetchTeams();
        } else {
            fetchAvailablePlayers();
        }
    }, [show, mode, fetchTeams, fetchAvailablePlayers]); /* ⊙ reference callbacks */

    /* -----------------------------------------------------------
       4) Debounced search
    ----------------------------------------------------------- */
    useEffect(() => {
        if (!show || mode === 'transfer') return;

        const id = setTimeout(() => fetchAvailablePlayers(searchTerm), 300);
        return () => clearTimeout(id);
    }, [searchTerm, show, mode, fetchAvailablePlayers]);

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        const timeout = setTimeout(() => {
            fetchAvailablePlayers(value);
        }, 500);

        setSearchTimeout(timeout);
    };

    const handleFilterChange = (e) => {
        setFilterStatus(e.target.value);
        setSearchTerm('');
        fetchAvailablePlayers('');
    };

    const handleClose = () => {
        setSelectedTeam(null);
        setSearchTerm('');
        setError(null);
        setFilterStatus('available');
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        onHide();
    };

    const handleAddPlayer = async () => {
        if (!selectedPlayer) return;

        try {
            const response = await axios.post(`/api/teams/${teamId}/players/`, {
                player_id: selectedPlayer.id
            }, {
                withCredentials: true,
            });

            if (response.status === 200) {
                onPlayerAdded(response.data);
                setSelectedPlayer(null);
                setSearchTerm('');
            }
        } catch (error) {
            console.error('Error adding player:', error);
            setError('Failed to add player to team');
        }
    };

    const handleTransferPlayer = async () => {
        if (!selectedPlayer || !selectedTeam) return;

        try {
            const response = await axios.put(`/api/teams/${teamId}/players/`, {
                player_id: selectedPlayer.id,
                new_team_id: selectedTeam
            }, {
                withCredentials: true,
            });

            if (response.status === 200) {
                onPlayerAdded(response.data);
                onHide();
            }
        } catch (error) {
            console.error('Error transferring player:', error);
            setError('Failed to transfer player');
        }
    };

    const handleRemovePlayer = async () => {
        if (!selectedPlayer) return;

        try {
            const response = await axios.delete(`/api/teams/${teamId}/players/`, {
                withCredentials: true,
                params: {
                    player_id: selectedPlayer.id
                }
            });

            if (response.status === 200) {
                onPlayerAdded(response.data);
                setSelectedPlayer(null);
                setSearchTerm('');
            }
        } catch (error) {
            console.error('Error removing player:', error);
            setError('Failed to remove player from team');
        }
    };

    if (!show) return null;

    return (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {mode === 'add' ? 'Add Player to Team' :
                                mode === 'transfer' ? 'Transfer Player' :
                                    'Remove Player from Team'}
                        </h5>
                        <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}

                        {mode === 'transfer' ? (
                            <div>
                                {selectedPlayer && (
                                    <div className="mb-4">
                                        <h6>Transferring Player:</h6>
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={selectedPlayer.profile_picture || `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`}
                                                alt={`${selectedPlayer.first_name} ${selectedPlayer.last_name}`}
                                                className="rounded-circle me-3"
                                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                            />
                                            <div>
                                                <h6 className="mb-0">{selectedPlayer.first_name} {selectedPlayer.last_name}</h6>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label className="form-label">Select Destination Team</label>
                                    <select
                                        className="form-select"
                                        value={selectedTeam || ''}
                                        onChange={(e) => setSelectedTeam(e.target.value)}
                                    >
                                        <option value="">Select a team...</option>
                                        {teams.map(team => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="row mb-3">
                                    <div className="col">
                                        <label htmlFor="playerSearch" className="form-label">Search Players</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="playerSearch"
                                            placeholder="Search by name or email..."
                                            value={searchTerm}
                                            onChange={handleSearch}
                                        />
                                    </div>
                                    {mode === 'add' && (
                                        <div className="col-auto">
                                            <label className="form-label">Filter</label>
                                            <select
                                                className="form-select"
                                                value={filterStatus}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="available">Available Only</option>
                                                <option value="all">All Players</option>
                                                <option value="on_team">On Team</option>
                                            </select>
                                        </div>
                                    )}
                                </div>

                                {loading ? (
                                    <div className="text-center">
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="list-group">
                                        {players.map((player) => (
                                            <button
                                                key={player.id}
                                                type="button"
                                                className={`list-group-item list-group-item-action d-flex align-items-center ${selectedPlayer?.id === player.id ? 'active' : ''
                                                    }`}
                                                onClick={() => setSelectedPlayer(player)}
                                            >
                                                <img
                                                    src={player.profile_picture || `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`}
                                                    alt={`${player.first_name} ${player.last_name}`}
                                                    className="rounded-circle me-3"
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                />
                                                <div className="text-start">
                                                    <h6 className="mb-0">{player.first_name} {player.last_name}</h6>
                                                    <small className="text-muted">{player.email}</small>
                                                </div>
                                            </button>
                                        ))}
                                        {players.length === 0 && !loading && (
                                            <div className="text-center text-muted py-3">
                                                No players found
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
                        {mode === 'add' && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAddPlayer}
                                disabled={!selectedPlayer}
                            >
                                Add Player
                            </button>
                        )}
                        {mode === 'transfer' && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleTransferPlayer}
                                disabled={!selectedTeam}
                            >
                                Transfer Player
                            </button>
                        )}
                        {mode === 'remove' && (
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={handleRemovePlayer}
                                disabled={!selectedPlayer}
                            >
                                Remove Player
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddPlayerModal; 